import { create } from 'zustand';
import {
  adGenerationApi,
  generationApi,
  type ActiveWorkflowsResponse,
  type QueueStatus,
  type GenerationRun,
  type RunDetailResponse,
  type GenerationBrief,
  type GenerationPlaybook,
  type CampaignDetailResponse,
  type AdPackStatusResponse,
} from '@/services/api';
import {
  subscribeToGenerationRunDetail,
  type GenerationRunDoc,
} from '@/services/firestoreListeners';
import type { Unsubscribe } from 'firebase/firestore';

interface BriefFilters {
  status: string;
  client_uid: string;
  persona_id: string;
  search: string;
  source_type: string;
}

// Module-level subscription reference (not part of state to avoid serialization issues)
let detailUnsubscribe: Unsubscribe | null = null;

interface GenerationState {
  // Dashboard
  workflows: ActiveWorkflowsResponse | null;
  queue: QueueStatus | null;
  runs: GenerationRun[];
  runsTotal: number;
  briefs: GenerationBrief[];
  briefsTotal: number;
  playbooks: GenerationPlaybook[];
  playbooksTotal: number;

  // Archive
  showArchived: boolean;
  archiving: string | null; // run_id being archived/unarchived

  // Detail
  detail: RunDetailResponse | null;

  // Real-time sync state
  isRealtime: boolean;

  // Briefs Explorer
  briefFilters: BriefFilters;
  briefsLoading: boolean;
  briefsError: string | null;
  selectedBrief: GenerationBrief | null;
  selectedBriefLoading: boolean;
  campaignDetails: Record<string, CampaignDetailResponse>; // playbookId -> detail
  /** Ad-generation pack jobs keyed by creative brief_id (wizard + API). */
  briefAdPackJobsByBriefId: Record<string, AdPackStatusResponse[]>;
  briefActionLoading: string | null; // briefId being acted upon

  loading: boolean;
  error: string | null;

  fetchDashboard: () => Promise<void>;
  fetchDetail: (runId: string) => Promise<void>;
  fetchBriefs: () => Promise<void>;
  fetchPlaybooks: () => Promise<void>;
  setShowArchived: (show: boolean) => void;
  archiveRun: (runId: string) => Promise<void>;
  unarchiveRun: (runId: string) => Promise<void>;
  clearError: () => void;

  // Real-time subscription
  subscribeToDetail: (runId: string) => void;
  unsubscribeDetail: () => void;

  // Briefs Explorer actions
  setBriefFilters: (filters: Partial<BriefFilters>) => void;
  clearBriefFilters: () => void;
  fetchBrief: (briefId: string) => Promise<void>;
  activateBrief: (briefId: string) => Promise<void>;
  archiveBrief: (briefId: string) => Promise<void>;
  versionBrief: (briefId: string) => Promise<GenerationBrief | null>;
  fetchCampaignDetail: (playbookId: string) => Promise<void>;
  fetchBriefAdPackJobs: (briefId: string) => Promise<void>;
  clearSelectedBrief: () => void;
}

const defaultBriefFilters: BriefFilters = {
  status: '',
  client_uid: '',
  persona_id: '',
  search: '',
  source_type: '',
};

export const useGenerationStore = create<GenerationState>((set, get) => ({
  workflows: null,
  queue: null,
  runs: [],
  runsTotal: 0,
  briefs: [],
  briefsTotal: 0,
  playbooks: [],
  playbooksTotal: 0,
  showArchived: false,
  archiving: null,
  detail: null,
  isRealtime: false,
  loading: false,
  error: null,

  // Briefs Explorer state
  briefFilters: { ...defaultBriefFilters },
  briefsLoading: false,
  briefsError: null,
  selectedBrief: null,
  selectedBriefLoading: false,
  campaignDetails: {},
  briefAdPackJobsByBriefId: {},
  briefActionLoading: null,

  fetchDashboard: async () => {
    const { showArchived } = get();
    set({ loading: true, error: null });
    try {
      const [workflows, queue, runsData] = await Promise.all([
        generationApi.getActiveWorkflows(),
        generationApi.getQueueStatus(),
        generationApi.listRuns(20, showArchived),
      ]);
      set({
        workflows,
        queue,
        runs: runsData.runs,
        runsTotal: runsData.total,
      });
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : 'Failed to load generation data',
      });
    } finally {
      set({ loading: false });
    }
  },

  fetchDetail: async (runId: string) => {
    set({ loading: true, error: null, detail: null });
    try {
      const detail = await generationApi.getWorkflowDetail(runId);
      set({ detail });
    } catch {
      // Fallback: try getting run directly
      try {
        const run = await generationApi.getRun(runId);
        set({
          detail: {
            run,
            execution_log: run.execution_log || { steps: [], total_steps: 0, steps_completed: 0 },
            tasks: [],
            assets: [],
            content: [],
          },
        });
      } catch (e2) {
        set({
          error: e2 instanceof Error ? e2.message : `Run ${runId} not found`,
        });
      }
    } finally {
      set({ loading: false });
    }
  },

  fetchBriefs: async () => {
    const { briefFilters } = get();
    set({ briefsLoading: true, briefsError: null });
    try {
      // Client-scoped inventory: do not pass persona_id. Workbench persona is
      // for URL/context and other APIs; Firestore briefs may be unassigned or
      // stamped with a different persona_id, so filtering here hid valid rows.
      const params: { client_uid?: string; status?: string; limit?: number } = { limit: 100 };
      if (briefFilters.status) params.status = briefFilters.status;
      if (briefFilters.client_uid) params.client_uid = briefFilters.client_uid;
      const data = await generationApi.listBriefs(params);
      set({ briefs: data.briefs, briefsTotal: data.total });
    } catch (e) {
      set({ briefsError: e instanceof Error ? e.message : 'Failed to load briefs' });
    } finally {
      set({ briefsLoading: false });
    }
  },

  fetchPlaybooks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await generationApi.listPlaybooks(50);
      set({ playbooks: data.playbooks, playbooksTotal: data.total });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load playbooks' });
    } finally {
      set({ loading: false });
    }
  },

  setShowArchived: (show: boolean) => {
    set({ showArchived: show });
    get().fetchDashboard();
  },

  archiveRun: async (runId: string) => {
    set({ archiving: runId, error: null });
    try {
      await generationApi.archiveRun(runId);
      // Refresh the list
      await get().fetchDashboard();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to archive run' });
    } finally {
      set({ archiving: null });
    }
  },

  unarchiveRun: async (runId: string) => {
    set({ archiving: runId, error: null });
    try {
      await generationApi.unarchiveRun(runId);
      // Refresh the list
      await get().fetchDashboard();
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to unarchive run' });
    } finally {
      set({ archiving: null });
    }
  },

  clearError: () => set({ error: null }),

  // Briefs Explorer actions
  setBriefFilters: (filters: Partial<BriefFilters>) => {
    set((state) => ({
      briefFilters: { ...state.briefFilters, ...filters },
    }));
    get().fetchBriefs();
  },

  clearBriefFilters: () => {
    set({ briefFilters: { ...defaultBriefFilters } });
    get().fetchBriefs();
  },

  fetchBrief: async (briefId: string) => {
    set({ selectedBriefLoading: true, briefsError: null, selectedBrief: null });
    try {
      const brief = await generationApi.getBrief(briefId);
      set({ selectedBrief: brief });
      void get().fetchBriefAdPackJobs(briefId);
    } catch (e) {
      set({ briefsError: e instanceof Error ? e.message : `Brief ${briefId} not found` });
    } finally {
      set({ selectedBriefLoading: false });
    }
  },

  fetchBriefAdPackJobs: async (briefId: string) => {
    try {
      const jobs = await adGenerationApi.listJobsForBrief(briefId);
      set((state) => ({
        briefAdPackJobsByBriefId: { ...state.briefAdPackJobsByBriefId, [briefId]: jobs },
      }));
    } catch {
      set((state) => ({
        briefAdPackJobsByBriefId: { ...state.briefAdPackJobsByBriefId, [briefId]: [] },
      }));
    }
  },

  activateBrief: async (briefId: string) => {
    set({ briefActionLoading: briefId, briefsError: null });
    try {
      const updated = await generationApi.activateBrief(briefId);
      set({ selectedBrief: updated });
      // Refresh the list
      await get().fetchBriefs();
    } catch (e) {
      set({ briefsError: e instanceof Error ? e.message : 'Failed to activate brief' });
    } finally {
      set({ briefActionLoading: null });
    }
  },

  archiveBrief: async (briefId: string) => {
    set({ briefActionLoading: briefId, briefsError: null });
    try {
      const updated = await generationApi.archiveBrief(briefId);
      set({ selectedBrief: updated });
      // Refresh the list
      await get().fetchBriefs();
    } catch (e) {
      set({ briefsError: e instanceof Error ? e.message : 'Failed to archive brief' });
    } finally {
      set({ briefActionLoading: null });
    }
  },

  versionBrief: async (briefId: string) => {
    set({ briefActionLoading: briefId, briefsError: null });
    try {
      const newBrief = await generationApi.versionBrief(briefId);
      // Refresh the list
      await get().fetchBriefs();
      return newBrief;
    } catch (e) {
      set({ briefsError: e instanceof Error ? e.message : 'Failed to create new version' });
      return null;
    } finally {
      set({ briefActionLoading: null });
    }
  },

  fetchCampaignDetail: async (playbookId: string) => {
    try {
      const detail = await generationApi.getCampaignDetail(playbookId);
      set((state) => ({
        campaignDetails: { ...state.campaignDetails, [playbookId]: detail },
      }));
      return;
    } catch {
      /* brief.playbook_ids are playbook IDs; /campaigns/{id} expects campaign_id */
    }
    try {
      const playbook = await generationApi.getPlaybook(playbookId);
      const meta = (playbook as { metadata?: Record<string, unknown>; brief_id?: string })
        .metadata;
      const campaignId =
        meta && typeof meta.campaign_id === 'string' && meta.campaign_id
          ? meta.campaign_id
          : '';
      if (campaignId) {
        try {
          const detail = await generationApi.getCampaignDetail(campaignId);
          set((state) => ({
            campaignDetails: { ...state.campaignDetails, [playbookId]: detail },
          }));
          return;
        } catch {
          /* fall through to playbook-shaped detail */
        }
      }
      const rawStatus = playbook.status as unknown;
      const statusStr =
        typeof rawStatus === 'string'
          ? rawStatus
          : rawStatus &&
              typeof rawStatus === 'object' &&
              rawStatus !== null &&
              'value' in rawStatus
            ? String((rawStatus as { value?: string }).value)
            : 'unknown';
      const pbBriefId =
        (playbook as { brief_id?: string }).brief_id ??
        '';
      set((state) => ({
        campaignDetails: {
          ...state.campaignDetails,
          [playbookId]: {
            campaign_id: campaignId || playbookId,
            brief_id: pbBriefId,
            playbook_id: playbookId,
            status: statusStr,
            asset_summary: {
              total_angles: 0,
              total_scripts: 0,
              total_images: 0,
              total_videos: 0,
              total_assets: playbook.asset_summary?.total_assets ?? 0,
              total_static_ads: 0,
              total_creative_reviews: 0,
            },
            cost_summary: { estimated_cost_usd: 0, actual_cost_usd: 0 },
            ranked_ads: [],
          },
        },
      }));
    } catch {
      /* Silently fail */
    }
  },

  clearSelectedBrief: () => {
    set({ selectedBrief: null, selectedBriefLoading: false, briefsError: null });
  },

  // Real-time subscription for run detail
  subscribeToDetail: (runId: string) => {
    // Clean up any existing subscription
    if (detailUnsubscribe) {
      detailUnsubscribe();
      detailUnsubscribe = null;
    }

    set({ loading: true, error: null, detail: null, isRealtime: false });

    // Helper to convert Firestore doc to RunDetailResponse
    const mapDocToDetail = (doc: GenerationRunDoc): RunDetailResponse => ({
      run: {
        run_id: doc.run_id,
        playbook_id: '',
        brief_id: '',
        client_uid: doc.client_uid,
        status: doc.status,
        run_type: doc.run_type,
        source_pipeline: doc.source_pipeline,
        source_job_id: doc.source_job_id,
        started_at: doc.started_at,
        completed_at: doc.completed_at,
        created_at: doc.created_at,
        archived: doc.archived,
        archived_at: doc.archived_at,
        progress: doc.progress ? {
          percent_complete: doc.progress.percent_complete ?? 0,
          elapsed_seconds: doc.progress.elapsed_seconds,
        } : undefined,
        execution_log: doc.execution_log ? {
          current_step: doc.execution_log.current_step,
          steps_completed: doc.execution_log.steps_completed ?? 0,
          total_steps: doc.execution_log.total_steps ?? 0,
          steps: (doc.execution_log.steps ?? []).map((s) => ({
            step_name: s.step_name,
            status: s.status,
            started_at: s.started_at,
            completed_at: s.completed_at,
            duration_seconds: s.started_at && s.completed_at
              ? (new Date(s.completed_at).getTime() - new Date(s.started_at).getTime()) / 1000
              : undefined,
            items_total: s.items_total ?? 0,
            items_completed: s.items_completed ?? 0,
            items_failed: s.items_failed ?? 0,
            message: s.message,
            error: s.error,
          })),
        } : { steps: [], total_steps: 0, steps_completed: 0 },
        metadata: doc.metadata as Record<string, string | number | boolean> | undefined,
        errors: doc.errors,
      },
      execution_log: doc.execution_log ? {
        current_step: doc.execution_log.current_step,
        steps_completed: doc.execution_log.steps_completed ?? 0,
        total_steps: doc.execution_log.total_steps ?? 0,
        steps: (doc.execution_log.steps ?? []).map((s) => ({
          step_name: s.step_name,
          status: s.status,
          started_at: s.started_at,
          completed_at: s.completed_at,
          duration_seconds: s.started_at && s.completed_at
            ? (new Date(s.completed_at).getTime() - new Date(s.started_at).getTime()) / 1000
            : undefined,
          items_total: s.items_total ?? 0,
          items_completed: s.items_completed ?? 0,
          items_failed: s.items_failed ?? 0,
          message: s.message,
          error: s.error,
        })),
      } : { steps: [], total_steps: 0, steps_completed: 0 },
      tasks: [],
      assets: [],
      content: [],
    });

    try {
      detailUnsubscribe = subscribeToGenerationRunDetail(
        runId,
        (doc) => {
          if (doc) {
            set({ detail: mapDocToDetail(doc), loading: false, isRealtime: true });
          } else {
            // Document not found in Firestore, fallback to REST API
            console.log(`[Generation] Run ${runId} not in Firestore, falling back to REST`);
            get().fetchDetail(runId);
          }
        },
        (error) => {
          console.error('[Generation] Firestore subscription error:', error);
          // Fallback to REST API on error
          get().fetchDetail(runId);
        },
      );
    } catch (error) {
      console.error('[Generation] Failed to subscribe to Firestore:', error);
      // Fallback to REST API
      get().fetchDetail(runId);
    }
  },

  unsubscribeDetail: () => {
    if (detailUnsubscribe) {
      detailUnsubscribe();
      detailUnsubscribe = null;
    }
    set({ isRealtime: false });
  },
}));
